// The picked areas, shared by kartvy and listvy and kept across reloads (localStorage), plus each view's own
// state for a reload (sessionStorage: map position, tabs, page …).
//
// The two views keep their picks differently: kartvy as kommun / län / postort / area entries with their RegSO ids
// (`selectedSearchAreas`, areas clicked off in `excludedSearchAreaIds`), listvy as search suggestions
// (kommun, ort, län, postort, omrade, omrade-word, omrade-map; clicked-off areas per suggestion in
// `excludedRegsoIds`). The saved form is a list of picks { kind, name, regsoIds, place, kartvy?, listvy? }:
// each view saves its own object with it (so it comes back exactly) and rebuilds the other view's from the
// kind, name and areas.

import { findLan } from './lanAreas.js';

const SELECTION_KEY = 'area_selection_v1';
const VIEW_STATE_KEY = 'view_state_v1';

const readJson = (storage, key) => {
    try {
        const raw = window[storage].getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
};
const writeJson = (storage, key, value) => {
    try {
        window[storage].setItem(key, JSON.stringify(value));
    } catch (error) {
        // Private mode or full storage: the state just isn't kept.
    }
};

const cleanIds = (ids) => [...new Set((Array.isArray(ids) ? ids : []).map((id) => String(id || '').trim()).filter(Boolean))];

// A fresh navigation to a bare URL — typed in, a bookmark, a link from another site, or a new tab — opens the
// site clean: the landing screen in listvy / an empty map in kartvy, even when a selection is saved. A reload,
// a back/forward or an in-app view switch instead keeps the saved selection ("where they were"). We tell a
// fresh navigation from a reload with the Navigation Timing type, and remember a fresh bare entry per tab in
// sessionStorage so refreshing while still on the landing stays on the landing; it clears the moment the user
// makes a selection (writeSelection), so from then on a reload restores as usual.
const FRESH_ENTRY_KEY = 'fresh_bare_entry_v1';

const navigationType = () => {
    try {
        const entries = performance.getEntriesByType('navigation');
        if (entries && entries.length && entries[0].type) return entries[0].type;
    } catch (error) { /* no Navigation Timing level 2 */ }
    try {
        const legacy = performance.navigation ? performance.navigation.type : null;
        if (legacy === 0) return 'navigate';
        if (legacy === 1) return 'reload';
        if (legacy === 2) return 'back_forward';
    } catch (error) { /* no legacy timing */ }
    return 'reload'; // unknown: keep the saved selection (the safe default)
};

try {
    // A bare URL has no query string (a shared listing link carries ?bostad=/?annons= and is handled on its own).
    if (typeof window !== 'undefined' && !window.location.search && navigationType() === 'navigate') {
        window.sessionStorage.setItem(FRESH_ENTRY_KEY, '1');
    }
} catch (error) {
    // Private mode / no sessionStorage: fall back to the normal restore-on-load behaviour.
}

const isFreshBareEntry = () => {
    try {
        return window.sessionStorage.getItem(FRESH_ENTRY_KEY) === '1';
    } catch (error) {
        return false;
    }
};

const clearFreshEntry = () => {
    try {
        window.sessionStorage.removeItem(FRESH_ENTRY_KEY);
    } catch (error) { /* ignore */ }
};

const EMPTY_SELECTION = { picks: [], excluded: [], savedBy: null };

// { picks, excluded, savedBy } or an empty selection. A fresh bare entry reads as empty, so the saved areas
// aren't restored and the landing shows; the saved selection stays in storage (it isn't wiped) and comes back
// on a reload once the user has made a pick this session.
export const readSavedSelection = () => {
    if (isFreshBareEntry()) return { ...EMPTY_SELECTION };
    const saved = readJson('localStorage', SELECTION_KEY);
    return {
        picks: Array.isArray(saved?.picks) ? saved.picks.filter((pick) => pick && pick.name) : [],
        excluded: cleanIds(saved?.excluded),
        savedBy: saved?.savedBy || null,
    };
};

const writeSelection = (picks, excluded, savedBy) => {
    // The user picked something: this tab is no longer a fresh landing, so later reloads restore as usual.
    if (Array.isArray(picks) && picks.length) clearFreshEntry();
    writeJson('localStorage', SELECTION_KEY, { v: 1, picks, excluded: cleanIds(excluded), savedBy, savedAt: Date.now() });
};

// A view's own state for a reload: readViewState('kartvy') → {} when nothing is saved.
export const readViewState = (view) => readJson('sessionStorage', VIEW_STATE_KEY)?.[view] || {};
export const saveViewState = (view, patch) => {
    const all = readJson('sessionStorage', VIEW_STATE_KEY) || {};
    all[view] = { ...(all[view] || {}), ...patch };
    writeJson('sessionStorage', VIEW_STATE_KEY, all);
};

// ---------- kartvy ----------

const KARTVY_KINDS = new Set(['kommun', 'lan', 'postort']);

export const saveKartvySelection = (entries, excluded) => {
    const picks = (entries || []).map((entry) => ({
        kind: KARTVY_KINDS.has(entry.type) ? entry.type : 'area',
        name: entry.name,
        place: entry.place || '',
        regsoIds: cleanIds(entry.regsoIds),
        kartvy: entry,
    }));
    writeSelection(picks, excluded, 'kartvy');
};

// Kartvy entries for the saved picks. A kommun or län picked in listvy may have no areas yet: it's matched by
// name in findKommun(name, kind) → entry | null (kartvy's search index), and left out while that isn't loaded.
export const kartvyEntriesFromSaved = (saved, findKommun = () => null) => {
    const entries = [];
    let pending = 0;
    saved.picks.forEach((pick, index) => {
        if (pick.kartvy?.regsoIds?.length) {
            entries.push(pick.kartvy);
            return;
        }
        if ((pick.kind === 'kommun' || pick.kind === 'lan') && !pick.regsoIds?.length) {
            const entry = findKommun(pick.name, pick.kind);
            if (entry) entries.push(entry);
            else pending += 1;
            return;
        }
        const regsoIds = cleanIds(pick.regsoIds);
        if (!regsoIds.length) return;
        const type = KARTVY_KINDS.has(pick.kind) ? pick.kind : 'area';
        const lan = type === 'lan' ? findLan(pick.name) : null;
        entries.push({
            id: regsoIds.length === 1 ? `map:${regsoIds[0]}` : `saved:${type}:${pick.name}:${index}`,
            type,
            name: lan ? lan.label : pick.name,
            ...(lan ? { lanCode: lan.code } : {}),
            // A kommun / län is self-describing, so it carries no place context (also cleans older saves).
            place: (type === 'kommun' || type === 'lan') ? '' : (pick.place || ''),
            regsoIds,
        });
    });
    const unique = [];
    const seen = new Set();
    entries.forEach((entry) => {
        if (seen.has(entry.id)) return;
        seen.add(entry.id);
        unique.push(entry);
    });
    return { entries: unique, pending };
};

// ---------- the area page ----------

// "Visa bostäder" / "Se på kartan" on an area page (src/area/AreaPage.js): the area becomes the search in both
// views, in place of what was picked before (saved by 'areaPage', so the view fits the map to it).
export const saveAreaPagePick = ({ code, name, kommun }) => saveAreaPagePicks([{ code, name, kommun }]);

// The same for several areas at once (a shared comparison's "Visa alla bostäder", src/area/ComparePage.js).
export const saveAreaPagePicks = (areas) => {
    writeSelection((areas || []).map(({ code, name, kommun }) => (
        { kind: 'area', name, place: kommun || '', regsoIds: cleanIds([code]) }
    )), [], 'areaPage');
};

// ---------- listvy ----------

const LISTVY_KIND = { kommun: 'kommun', postort: 'postort', lan: 'lan', ort: 'ort' };

// coverage: listvy's regsoCoverageBySuggestionId (the areas each suggestion covers, clicked-off ones left out).
export const saveListvySelection = (suggestions, coverage) => {
    const excluded = [];
    const picks = (suggestions || []).map((suggestion) => {
        const own = cleanIds(suggestion.excludedRegsoIds);
        excluded.push(...own);
        const covered = coverage?.get?.(String(suggestion.id || '').trim())?.regsoIds;
        const regsoIds = cleanIds([
            ...(suggestion.regsoId ? [suggestion.regsoId] : []),
            ...(Array.isArray(suggestion.regsoIds) ? suggestion.regsoIds : []),
            ...(covered ? [...covered] : []),
            ...own,
        ]);
        const kind = LISTVY_KIND[suggestion.type] || 'area';
        const name = kind === 'kommun' ? (suggestion.kommun || suggestion.label)
            : kind === 'postort' ? (suggestion.postort || suggestion.label)
                : kind === 'lan' ? (suggestion.lan || suggestion.label)
                    : kind === 'ort' ? (suggestion.ort || suggestion.label)
                        : String(suggestion.label || '').split(' · ')[0];
        // A kommun or län is self-describing ("X kommun"); only sub-areas (ort, postort, område) carry their
        // kommun as a muted place context, so kartvy's chip doesn't read "Stockholm kommun · Stockholm".
        const place = (kind === 'kommun' || kind === 'lan') ? '' : (suggestion.kommun || '');
        return { kind, name, place, regsoIds, listvy: suggestion };
    });
    writeSelection(picks, excluded, 'listvy');
};

// Listvy suggestions for the saved picks (clicked-off areas go to the suggestions that cover them).
export const listvySuggestionsFromSaved = (saved, normalize = (text) => String(text || '').toLowerCase()) => {
    const excluded = new Set(saved.excluded);
    const suggestions = saved.picks.map((pick) => {
        const regsoIds = cleanIds(pick.regsoIds);
        const ownExcluded = regsoIds.filter((id) => excluded.has(id));
        if (pick.listvy?.id) {
            return { ...pick.listvy, excludedRegsoIds: ownExcluded.length ? ownExcluded : pick.listvy.excludedRegsoIds };
        }
        const base = { kommun: '', ort: '', lan: '', regsoId: '', postort: '' };
        const withExcluded = (suggestion) => (ownExcluded.length ? { ...suggestion, excludedRegsoIds: ownExcluded } : suggestion);
        if (pick.kind === 'kommun') {
            return withExcluded({ ...base, id: `kommun:${pick.name}`, type: 'kommun', label: `${pick.name} kommun`, kommun: pick.name });
        }
        if (pick.kind === 'lan') {
            const lan = findLan(pick.name);
            if (lan) {
                return withExcluded({ ...base, id: `lan:${lan.name}`, type: 'lan', label: lan.label, lan: lan.name, lanCode: lan.code });
            }
        }
        if (pick.kind === 'postort') {
            return withExcluded({
                ...base,
                id: `postort:${normalize(pick.name)}:saved`,
                type: 'postort',
                label: pick.place ? `${pick.name}, ${pick.place}` : pick.name,
                regsoIds,
                postort: pick.name,
            });
        }
        if (regsoIds.length === 1) {
            return { ...base, id: `omrade-map:${regsoIds[0]}`, type: 'omrade-map', label: pick.name, regsoId: regsoIds[0] };
        }
        if (!regsoIds.length) return null;
        return withExcluded({
            ...base,
            id: `omrade-word:${normalize(pick.name)}:saved`,
            type: 'omrade-word',
            label: pick.name,
            regsoIds,
        });
    }).filter(Boolean);
    const seen = new Set();
    return suggestions.filter((suggestion) => {
        if (seen.has(suggestion.id)) return false;
        seen.add(suggestion.id);
        return true;
    });
};
